import { useEffect, useRef } from 'react';
import { MusicAPIIntegrationID } from 'fym-shared/src/music-services/ids';
import { DistributorID } from 'fym-shared/src/smartlinks/catalog';
import { ServiceConnection } from '../../../redux/api/fymAPI.types';

/**
 * The service names of a `/catalog/connect/:service` link (design your-intelligence h4), and the
 * `integrationType` of each. The old app links used `spotify-for-artists`.
 */
const CONNECT_LINK_SERVICES: Record<string, string> = {
  'spotify-artists': MusicAPIIntegrationID.Spotify,
  'spotify-for-artists': MusicAPIIntegrationID.Spotify,
  spotify: MusicAPIIntegrationID.Spotify,
  'deezer-creators': MusicAPIIntegrationID.Deezer,
  deezer: MusicAPIIntegrationID.Deezer,
  youtube: MusicAPIIntegrationID.YoutubeMusic,
  youtubeMusic: MusicAPIIntegrationID.YoutubeMusic,
  [DistributorID.Ditto]: DistributorID.Ditto,
};

/** The `integrationType` of a connect link service, or undefined for a name we do not know. */
export function integrationTypeOfConnectLink(service: string | undefined): string | undefined {
  return service === undefined ? undefined : CONNECT_LINK_SERVICES[service];
}

/**
 * Opens the connect of the service that a `/catalog/connect/:service` link names, one time, when
 * the cards are loaded. An unknown service opens nothing: the Catalog shows as usual.
 */
export function useCatalogConnectLink({
  service,
  connections,
  onConnect,
}: {
  service: string | undefined;
  connections: ServiceConnection[] | undefined;
  onConnect: (integrationType: string) => void;
}): void {
  const openedRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    const integrationType = integrationTypeOfConnectLink(service);
    if (integrationType === undefined || connections === undefined || openedRef.current === service) {
      return;
    }
    if (!connections.some((connection) => connection.integrationType === integrationType)) {
      return;
    }
    openedRef.current = service;
    onConnect(integrationType);
  }, [connections, onConnect, service]);
}
